/**
 * AuditIntegrityPill — site-wide passive trust mark.
 *
 * A tiny one-line variant of <AuditIntegrityBadge/>. Lives in the
 * PublicFooter so it appears on every public page. Renders ONLY when
 * the chain verifies (ok===true) — if the endpoint is down or the
 * chain is broken, we render NOTHING. Silence is safer than a
 * red flag on the marketing surface.
 *
 * Clickable → /security (where the full badge lives).
 *
 * Backed by GET /api/security/audit/verify/public (60s cache).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export default function AuditIntegrityPill({ className = '' }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/security/audit/verify/public`, { credentials: 'omit' })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (!cancelled && j && j.ok) setData(j); })
      .catch(() => { /* silent — graceful hide */ });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  return (
    <Link
      to="/security"
      data-testid="audit-integrity-pill"
      title={`Audit log chain verified · ${Number(data.total).toLocaleString()} entries · click for live integrity check`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors ${className}`}
    >
      <ShieldCheck size={11} strokeWidth={2.5} />
      <span>Audit Integrity Verified</span>
      <span className="opacity-60 font-mono">· {Number(data.total).toLocaleString()}</span>
    </Link>
  );
}
